/**
 * Health Diary Modal Component
 * 헤더 영역의 "일기" 버튼에서 열리는 모달
 * - 오늘의 일기 표시
 * - 일기 작성/수정 기능
 */

import React, { useState, useEffect } from 'react';
import * as healthDiaryService from '../services/healthDiaryService';

interface HealthDiaryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DiaryData {
  id: string;
  diary_date: string;
  content: string;
  created_at: string;
  updated_at: string;
  isNew?: boolean;
  action?: 'created' | 'updated';
}

export const HealthDiaryModal: React.FC<HealthDiaryModalProps> = ({ isOpen, onClose }) => {
  const [diary, setDiary] = useState<DiaryData | null>(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [todayDate, setTodayDate] = useState('');

  // 모달이 열릴 때 오늘의 일기 조회
  useEffect(() => {
    if (isOpen) {
      loadTodayDiary();
    }
  }, [isOpen]);

  // 오늘의 일기 불러오기
  const loadTodayDiary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await healthDiaryService.getTodayDiary();
      
      if (response.data) {
        setDiary(response.data);
        setContent(response.data.content);
      } else {
        setDiary(null);
        setContent('');
      }
      
      if (response.todayDate) {
        setTodayDate(response.todayDate);
      }
    } catch (err) {
      console.error('일기 조회 실패:', err);
      setError('오늘의 일기를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 일기 저장/수정
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await healthDiaryService.saveDiary(content);
      
      if (response.success) {
        setDiary(response.data);
        setSuccess(response.message || '저장되었습니다.');
        
        // 2초 후 모달 닫기
        setTimeout(() => {
          setSuccess(null);
          onClose();
        }, 2000);
      }
    } catch (err) {
      console.error('일기 저장 실패:', err);
      setError(err instanceof Error ? err.message : '일기 저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 일기 삭제
  const handleDelete = async () => {
    if (!diary) return;
    
    if (!window.confirm('이 일기를 삭제하시겠습니까?')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await healthDiaryService.deleteDiary(diary.id);
      
      setDiary(null);
      setContent('');
      setSuccess('일기가 삭제되었습니다.');
      
      setTimeout(() => {
        setSuccess(null);
        onClose();
      }, 2000);
    } catch (err) {
      console.error('일기 삭제 실패:', err);
      setError(err instanceof Error ? err.message : '일기 삭제 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // 날짜 포매팅
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    const dayName = dayNames[date.getDay()];
    return `${year}년 ${month}월 ${day}일 (${dayName})`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] flex flex-col shadow-2xl">
        {/* 헤더 */}
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-white rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-slate-900">오늘의 건강 일기</h2>
            <p className="text-xs text-slate-500 mt-1">{formatDate(todayDate || new Date().toISOString())}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors"
            aria-label="닫기"
          >
            ✕
          </button>
        </div>

        {/* 컨텐츠 */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-sm text-slate-500">일기를 불러오는 중...</p>
              </div>
            </div>
          ) : (
            <>
              {/* 에러 메시지 */}
              {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              )}

              {/* 성공 메시지 */}
              {success && (
                <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200">
                  <p className="text-xs text-green-700">{success}</p>
                </div>
              )}

              {/* 일기 작성 영역 */}
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  {diary ? '오늘의 건강 기록을 수정하세요' : '오늘의 건강 기록을 작성하세요'}
                </label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="오늘 하루의 건강 상태, 식단, 운동, 기분 등을 자유롭게 기록하세요."
                  className="w-full h-48 p-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none text-sm text-slate-900"
                  disabled={saving || loading}
                />
                <div className="mt-2 text-xs text-slate-500">
                  {content.length}/2000
                </div>
              </div>

              {/* 작성 정보 */}
              {diary && (
                <div className="mt-4 p-3 rounded-lg bg-slate-50 text-xs text-slate-600">
                  <p>작성일: {new Date(diary.created_at).toLocaleString('ko-KR')}</p>
                  {diary.updated_at !== diary.created_at && (
                    <p>수정일: {new Date(diary.updated_at).toLocaleString('ko-KR')}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* 하단 버튼 */}
        <div className="sticky bottom-0 border-t border-slate-100 px-6 py-4 bg-white rounded-b-2xl flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
            disabled={saving || loading}
          >
            취소
          </button>
          {diary && (
            <button
              onClick={handleDelete}
              className="flex-1 h-10 rounded-lg border border-red-200 text-red-600 font-semibold text-sm hover:bg-red-50 transition-colors disabled:opacity-50"
              disabled={saving || loading}
            >
              삭제
            </button>
          )}
          <button
            onClick={handleSave}
            className="flex-1 h-10 rounded-lg bg-violet-600 text-white font-semibold text-sm hover:bg-violet-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            disabled={!content.trim() || saving || loading}
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                저장 중...
              </>
            ) : diary ? (
              '수정 완료'
            ) : (
              '저장하기'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HealthDiaryModal;
